import React, { useMemo } from "react";
import { Avatar as UiAvatar, AvatarFallback } from "../../components/ui/avatar";

const toAbsoluteUrl = (u) => {
  if (!u) return null;
  const s = String(u).trim();

  // already absolute?
  if (/^(https?:)?\/\//i.test(s)) return s;

  // allow only http(s) – prevents javascript: etc.
  // prepend https by default
  return `https://${s}`;
};

/* helpers */
const normalize = (s = "") =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const fmtDateTime = (date, time) => {
  if (!date && !time) return null;
  const dt = date && time ? new Date(`${date}T${time}`) : null;
  return dt && !Number.isNaN(dt.getTime())
    ? dt.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
    : `${date ?? ""} ${time ?? ""}`.trim();
};

const safeJSON = (v) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v;
  } catch {
    return null;
  }
};

const AgentAvatar = ({ name = "" }) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

  return (
    <UiAvatar className="h-10 w-10 border">
      <AvatarFallback>{initials || "A"}</AvatarFallback>
    </UiAvatar>
  );
};

/* show meeting details ONLY on this step */
const MEETING_STEP = normalize("Meeting - discuss relocation wish list");

/* show advice paragraph on this cluster of steps */
const ADVICE_CLUSTER = new Set([
  normalize("online meeting with agent"),
  normalize("Meeting link sent to transferee (Teams)"),
  MEETING_STEP,
]);

const ADVICE_CONTENT = {
  heading: "About this meeting",
  bullets: [
    "Align on your needs, timing, and budget.",
    "Discuss areas, commute, schools and lifestyle preferences.",
    "Agree on property types and shortlisting criteria.",
  ],
  prepHeading: "How to prepare",
  prepBullets: [
    "Think about must-haves vs nice-to-haves (bedrooms, parking, pets, etc.).",
    "List any preferred suburbs or areas to avoid.",
    "Have your target move-in window and budget handy.",
    "Collect links to any example properties you like (optional).",
  ],
  extraNote:
    "If your company has housing policy documents, please share them before or during the call.",
};

const ADVICE_TENANT_CONTENT = {
  heading: "About the tenant profile",
  bullets: [
    "This form helps us verify details and match you with suitable properties.",
    "Provide accurate info for all occupants (IDs, employment, pets).",
    "Upload supporting documents where requested (income, references).",
  ],
  prepHeading: "Before you start",
  prepBullets: [
    "Have IDs, proof of income, and references ready.",
    "Confirm all occupant and pet details.",
    "Double-check contact and emergency contact info.",
    "If unsure about a field, leave a note for your agent in comments.",
  ],
  extraNote:
    "You can save and return later — submit once all required fields are complete.",
};

/* SWIFT verification helper (static) */
/** Handles a few wording variants so minor text changes still match */
const SWIFT_TITLES = new Set([
  normalize("Verbally confirm agency bank & SWIFT code"),
  normalize("Verbally confirm agency and bank SWIFT code"),
  normalize("Verbally confirm agency bank & SWIT code"), // common typo
]);

const SWIFT_HELPER_CONTENT = {
  heading: "Security tip: confirm bank details verbally",
  bullets: [
    "Call your agent using the number already on file — do not use phone numbers in a new/unexpected email.",
    "Ask the agent to read out the account name and BSB/Account (AU) or IBAN/SWIFT (international).",
    "Compare every digit with what you were sent; watch for small letter/number swaps.",
    "If anything differs, stop and contact your agent via a known channel.",
  ],
  extraNote:
    "Never rely solely on email for payment details. If needed, send a small test amount first and confirm receipt.",
};

const ADVICE_RENTAL_APP_CONTENT = {
  heading: "Apply to shortlisted properties",
  bullets: [
    "Apply via the listing platform (realestate.com.au / Domain) or the agency website.",
    "Use the tenant profile details you prepared earlier; include all occupants.",
    "Attach supporting docs: photo ID/passport, employment/visa, payslips or bank statements, rental history/references, and pet documents (if any).",
    "Upload documents requested by your relocation team in the Documents section for your project.",
  ],
  followHeading: "After you submit",
  followBullets: [
    "Choose Proceed with application in Properties & viewings, and share your application reference with your team.",
    "Use portal Messages to follow up with your relocation team.",
    "Do not pay application fees or holding deposits until your agent confirms approval and bank details have been verbally verified.",
  ],
  extraNote:
    "Your team can view documents uploaded to your project. If you use an external folder, share it only with the intended recipients.",
};

const AGENT_TITLE_RELOCATION = normalize("Your assigned relocation agent");

/* client-side feature title matchers */
const HANDOVER_TITLES = new Set([
  normalize("View Handover of Rental Profile Details"),
  normalize("Handover of Rental Profile Details"),
  normalize("View handover of rental profile details"),
]);

const QUOTE_REVIEW_TITLES = new Set([
  normalize("Review your quote"),
  normalize("Review quote"),
  normalize("Review & approve quote"),
  normalize("View quote"),
]);

const QUOTE_ACCEPTED_TITLES = new Set([
  normalize("Quote accepted"),
  normalize("Quote has been accepted"),
]);

const ADVICE_RENTAL_APP_TITLES = new Set([
  normalize("Apply and follow up with agents"),
  normalize("Apply and follow up with agent"),
  normalize("Apply & follow up with agents"),
]);

/* component */
export default function TaskInfoBox({
  currentTask,
  allTasks = [],
  currentStep,
  clientTypeId,
  agentDetails,
  properties = [],
  airportDetails = null,
  insurances = [],
  viewings = [],
  cleanerBooking = null,
  rubbishBooking = null,
  hotelTransfer = null,
  elevatorAccess = null,
  declutterBooking = null,
  boxDelivery = null,
}: any) {
  const title = currentTask?.client_title ?? currentTask?.title ?? "";
  const normTitle = normalize(title);
  const taskKind = currentTask?.task_kind || "general";

  // MEETING DETAILS: only on the wish-list step
  const meeting = useMemo(() => {
    if (taskKind !== "meeting_details") return null;

    // 1) current task's JSON
    if (
      currentTask?.meeting_details &&
      String(currentTask.meeting_details).trim() !== ""
    ) {
      return safeJSON(currentTask.meeting_details);
    }

    // 2) otherwise latest task that has meeting_details
    const cand = (allTasks || [])
      .filter(
        (t) => t.meeting_details && String(t.meeting_details).trim() !== "",
      )
      .sort((a, b) => Number(b.task_number) - Number(a.task_number));

    return cand[0] ? safeJSON(cand[0].meeting_details) : null;
  }, [taskKind, currentTask, allTasks]);

  // ECR LINK: prefer current task's link, else latest among all tasks
  const ecrLink = useMemo(() => {
    const here = currentTask?.ECR_link && String(currentTask.ECR_link).trim();
    if (here) return currentTask.ECR_link;

    const cand = (allTasks || [])
      .filter((t) => t.ECR_link && String(t.ECR_link).trim() !== "")
      .sort((a, b) => Number(b.task_number) - Number(a.task_number));

    return cand[0]?.ECR_link || null;
  }, [currentTask, allTasks]);

  const quoteLink = useMemo(() => {
    const here =
      currentTask?.quote_link && String(currentTask.quote_link).trim();
    if (here) return currentTask.quote_link;

    const cand = (allTasks || [])
      .filter((t) => t.quote_link && String(t.quote_link).trim() !== "")
      .sort((a, b) => Number(b.task_number) - Number(a.task_number));

    return cand[0]?.quote_link || null;
  }, [currentTask, allTasks]);

  // ----- STEP GATES -----
  const showEcrLink = taskKind === "ecr" && !!ecrLink;

  // --- split example vs viewing props ---
  // treat any of these shapes as "has a scheduled viewing"
  const hasInspection = (p: any = {}) =>
    Boolean(
      p.inspection_date ||
      p.inspection_time ||
      p.viewing_date ||
      p.viewing_time ||
      (p.inspection && (p.inspection.date || p.inspection.time)),
    );

  // prefer explicit arrays but fall back to the other if empty
  const sourceForViewings = Array.isArray(viewings) ? viewings : [];
  const sourceForExamples = Array.isArray(properties) ? properties : [];
  const viewingProps = (
    Array.isArray(sourceForViewings) ? sourceForViewings : []
  ).filter(hasInspection);
  const exampleProps = (
    Array.isArray(sourceForExamples) ? sourceForExamples : []
  ).filter((p) => !hasInspection(p));

  const getInspectionDate = (p: any = {}) =>
    p.inspection_date || p.viewing_date || p.inspection?.date || null;
  const getInspectionTime = (p: any = {}) =>
    p.inspection_time || p.viewing_time || p.inspection?.time || null;

  const showExampleProps = taskKind === "properties" && exampleProps.length > 0;
  const showViewings =
    taskKind === "property_viewings" && viewingProps.length > 0;

  // Meeting advice cluster
  const showAdvice = ADVICE_CLUSTER.has(normTitle);

  // Tenant-profile advice (step 2)
  const IS_STEP_TWO = Number(currentStep ?? currentTask?.task_number) === 2;
  const showTenantAdvice = IS_STEP_TWO && Number(clientTypeId) !== 2;

  const showRentalApplyAdvice = ADVICE_RENTAL_APP_TITLES.has(normTitle);

  const showSwiftAdvice = SWIFT_TITLES.has(normTitle);
  const showQuoteReview = QUOTE_REVIEW_TITLES.has(normTitle);
  const showQuoteAccepted = QUOTE_ACCEPTED_TITLES.has(normTitle);
  const showCleaner = taskKind === "cleaner";
  const showRubbish = taskKind === "rubbish";
  const shouldShowAgent =
    Number(clientTypeId) === 1 &&
    normTitle === AGENT_TITLE_RELOCATION &&
    (agentDetails?.name || agentDetails?.email);

  /* pull extra data from tasks for client-side displays */
  // Utilities details: prefer current task, else latest any task
  const utilities = useMemo(() => {
    const fromHere =
      currentTask?.utility_details &&
      String(currentTask.utility_details).trim() !== ""
        ? currentTask.utility_details
        : null;
    if (fromHere)
      return typeof fromHere === "string" ? safeJSON(fromHere) : fromHere;

    const cand = (allTasks || [])
      .filter((t) => t.utility_details)
      .sort((a, b) => Number(b.task_number) - Number(a.task_number));

    if (!cand[0]) return null;
    const raw = cand[0].utility_details;
    return typeof raw === "string" ? safeJSON(raw) : raw;
  }, [currentTask, allTasks]);

  // Home visit info: prefer current task, else latest any task
  const homeVisit = useMemo(() => {
    const fromHere = currentTask?.home_visit_info;
    if (fromHere)
      return typeof fromHere === "string" ? safeJSON(fromHere) : fromHere;

    const cand = (allTasks || [])
      .filter((t) => t.home_visit_info)
      .sort((a, b) => Number(b.task_number) - Number(a.task_number));

    if (!cand[0]) return null;
    const raw = cand[0].home_visit_info;
    return typeof raw === "string" ? safeJSON(raw) : raw;
  }, [currentTask, allTasks]);

  const showHandover = HANDOVER_TITLES.has(normTitle);
  const showHomeVisit = taskKind === "home_visit";
  const showAirport = taskKind === "airport_pickup";
  const showUtilities = taskKind === "utilities";
  const showInsurance = taskKind === "insurance";
  const showElevator = taskKind === "elevator_access";
  const showDeclutter = taskKind === "declutter";
  const showBoxDelivery = taskKind === "box_delivery" && !!boxDelivery;

  return (
    <div className="w-full space-y-4">
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase text-muted-foreground">
          Current Step
        </p>
        <p className="text-base font-semibold text-foreground">
          {title || "N/A"}
        </p>
      </div>

      {shouldShowAgent && (
        <InfoPanel title="Your relocation agent">
          <div className="flex items-start gap-3">
            <AgentAvatar name={agentDetails?.name} />
            <div className="min-w-0 text-sm">
              {agentDetails?.name && (
                <div className="font-medium">{agentDetails.name}</div>
              )}
              {agentDetails?.email && (
                <a
                  href={`mailto:${agentDetails.email}`}
                  className="mt-0.5 block break-words text-primary underline-offset-4 hover:underline"
                >
                  {agentDetails.email}
                </a>
              )}
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Please reach out to{" "}
            <span className="font-medium text-foreground">
              {agentDetails?.name || "your agent"}
            </span>{" "}
            via the Slack chat
            {agentDetails?.email ? (
              <>
                {" "}
                or email{" "}
                <a
                  href={`mailto:${agentDetails.email}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {agentDetails.email}
                </a>
              </>
            ) : null}{" "}
            if you have any questions or need assistance.
          </p>
        </InfoPanel>
      )}

      {meeting && (
        <InfoPanel title="Upcoming meeting">
          {(meeting.date || meeting.time) && (
            <p className="text-sm text-muted-foreground">
              {fmtDateTime(meeting.date, meeting.time)}
            </p>
          )}
          {meeting.link && (
            <InfoLink href={toAbsoluteUrl(meeting.link)}>Join meeting</InfoLink>
          )}
          <InfoList className="mt-2">
            <InfoItem label="Attendees">{meeting.attendees}</InfoItem>
            <InfoItem label="Title">{meeting.title}</InfoItem>
            <InfoItem label="Notes">{meeting.notes}</InfoItem>
          </InfoList>
        </InfoPanel>
      )}

      {showEcrLink && (
        <InfoPanel title="ECR video">
          <InfoLink href={toAbsoluteUrl(ecrLink)}>Open ECR video</InfoLink>
          <p className="mt-2 text-xs text-muted-foreground">
            Tip: If OneDrive asks for access, set the link to "Anyone with the
            link".
          </p>
        </InfoPanel>
      )}

      {showAdvice && (
        <InfoPanel title={ADVICE_CONTENT.heading}>
          <Bullets items={ADVICE_CONTENT.bullets} />
          <SectionHeading>{ADVICE_CONTENT.prepHeading}</SectionHeading>
          <Bullets items={ADVICE_CONTENT.prepBullets} />
          <p className="mt-3 text-sm text-muted-foreground">
            {ADVICE_CONTENT.extraNote}
          </p>
        </InfoPanel>
      )}

      {showTenantAdvice && (
        <InfoPanel title={ADVICE_TENANT_CONTENT.heading}>
          <Bullets items={ADVICE_TENANT_CONTENT.bullets} />
          <SectionHeading>{ADVICE_TENANT_CONTENT.prepHeading}</SectionHeading>
          <Bullets items={ADVICE_TENANT_CONTENT.prepBullets} />
          <p className="mt-3 text-sm text-muted-foreground">
            {ADVICE_TENANT_CONTENT.extraNote}
          </p>
        </InfoPanel>
      )}

      {showSwiftAdvice && (
        <InfoPanel title={SWIFT_HELPER_CONTENT.heading}>
          <Bullets items={SWIFT_HELPER_CONTENT.bullets} />
          <p className="mt-3 text-sm text-muted-foreground">
            {SWIFT_HELPER_CONTENT.extraNote}
          </p>
        </InfoPanel>
      )}

      {showHandover && (
        <InfoPanel title="Handover of Rental Profile">
          <p className="text-sm text-muted-foreground">
            Please check your email for the{" "}
            <span className="font-medium text-foreground">
              Handover of Rental Profile Details
            </span>{" "}
            document. If it is not there, check your spam folder or ask your
            agent to resend.
            {agentDetails?.email && (
              <>
                {" "}
                You can also email{" "}
                <a
                  href={`mailto:${agentDetails.email}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {agentDetails.email}
                </a>
                .
              </>
            )}
          </p>
        </InfoPanel>
      )}

      {showHomeVisit && (
        <InfoPanel title="Home visit">
          {homeVisit ? (
            <InfoList>
              <InfoItem label="When">
                {fmtDateTime(homeVisit.visitDate, homeVisit.visitTime)}
              </InfoItem>
              <InfoItem label="Location">{homeVisit.address}</InfoItem>
              <InfoItem label="Contact">
                {[homeVisit.contactName, homeVisit.contactPhone]
                  .filter(Boolean)
                  .join(" ")}
              </InfoItem>
              <InfoItem label="Access">{homeVisit.accessNotes}</InfoItem>
              <InfoItem label="Purpose">{homeVisit.notes}</InfoItem>
            </InfoList>
          ) : (
            <p className="text-sm text-muted-foreground">
              Your agent has not added home visit details yet.
            </p>
          )}
        </InfoPanel>
      )}

      {showAirport && (
        <InfoPanel title="Airport pickup">
          {airportDetails ? (
            <InfoList>
              <InfoItem label="Flight">{airportDetails.flight_number}</InfoItem>
              <InfoItem label="Pickup location">
                {airportDetails.pickup_address}
              </InfoItem>
              <InfoItem label="Passengers">
                {Number(airportDetails.number_of_adults ?? 0)} adult(s),{" "}
                {Number(airportDetails.number_of_children ?? 0)} child(ren)
              </InfoItem>
              <InfoItem label="Bags">
                {Number(airportDetails.number_of_bags ?? 0)} total
                {airportDetails.additional_luggage
                  ? " + additional luggage"
                  : ""}
              </InfoItem>
            </InfoList>
          ) : (
            <p className="text-sm text-muted-foreground">
              Pickup instructions will appear here once your agent provides
              them.
            </p>
          )}
        </InfoPanel>
      )}

      {showUtilities && (
        <InfoPanel title="Internet & utilities information">
          {utilities ? (
            <div className="space-y-3">
              {["electricity", "gas", "water", "wifi"].map((key) => {
                const utility = utilities?.[key];
                if (!utility) return null;
                return (
                  <div
                    key={key}
                    className="rounded-md border bg-background p-3 text-sm"
                  >
                    <div className="font-medium capitalize">{key}</div>
                    <InfoList className="mt-1">
                      <InfoItem label="Available">
                        {utility.available ? "Yes" : "No"}
                      </InfoItem>
                      <InfoItem label="Notes">{utility.notes}</InfoItem>
                    </InfoList>
                    {utility.onedrive_link && (
                      <InfoLink href={toAbsoluteUrl(utility.onedrive_link)}>
                        View document
                      </InfoLink>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Utilities info will appear here when your agent uploads it.
            </p>
          )}
        </InfoPanel>
      )}

      {showInsurance && (
        <InfoPanel title="Contents & car insurance">
          {Array.isArray(insurances) && insurances.length > 0 ? (
            <ul className="space-y-3">
              {insurances.map((item, index) => (
                <li key={index} className="rounded-md border bg-background p-3">
                  <div className="font-medium">
                    {item.insurance_type || "Insurance"}
                  </div>
                  <InfoList className="mt-1">
                    <InfoItem label="Provider">{item.provider}</InfoItem>
                    <InfoItem label="Policy #">{item.policy_number}</InfoItem>
                    <InfoItem label="Period">
                      {[item.start_date, item.expiry_date]
                        .filter(Boolean)
                        .join(" - ")}
                    </InfoItem>
                    <InfoItem label="Notes">{item.notes}</InfoItem>
                  </InfoList>
                  {item.onedrive_link && (
                    <InfoLink href={toAbsoluteUrl(item.onedrive_link)}>
                      View policy document
                    </InfoLink>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              Insurance details will appear here once your agent provides them.
            </p>
          )}
        </InfoPanel>
      )}

      {showExampleProps && (
        <InfoPanel title="Example properties">
          <ul className="space-y-3">
            {exampleProps.slice(0, 5).map((property, index) => (
              <li
                key={property.id ?? index}
                className="rounded-md border bg-background p-3"
              >
                <div className="font-medium">{property.title}</div>
                {property.link && (
                  <InfoLink href={toAbsoluteUrl(property.link)}>
                    View listing
                  </InfoLink>
                )}
                {property.address && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {property.address}
                  </p>
                )}
                <MetricPills item={property} />
                {property.notes && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {property.notes}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </InfoPanel>
      )}

      {showViewings && (
        <InfoPanel title="Property viewings">
          <ul className="space-y-3">
            {viewingProps.slice(0, 10).map((viewing, index) => (
              <li
                key={viewing.id ?? index}
                className="rounded-md border bg-background p-3"
              >
                <div className="font-medium">{viewing.title}</div>
                <InfoList className="mt-1">
                  <InfoItem label="Inspection">
                    {fmtDateTime(
                      getInspectionDate(viewing),
                      getInspectionTime(viewing),
                    )}
                  </InfoItem>
                  <InfoItem label="Address">{viewing.address}</InfoItem>
                </InfoList>
                <MetricPills item={viewing} />
                <div className="mt-2 flex flex-wrap gap-3">
                  {viewing.link && (
                    <InfoLink href={toAbsoluteUrl(viewing.link)}>
                      View listing
                    </InfoLink>
                  )}
                  {viewing.walkthrough_video_link && (
                    <InfoLink
                      href={toAbsoluteUrl(viewing.walkthrough_video_link)}
                    >
                      Watch walkthrough
                    </InfoLink>
                  )}
                </div>
                {viewing.notes && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {viewing.notes}
                  </p>
                )}
              </li>
            ))}
          </ul>
          {viewingProps.length > 10 && (
            <p className="mt-2 text-xs text-muted-foreground">
              Showing first 10 viewings.
            </p>
          )}
        </InfoPanel>
      )}

      {showQuoteReview && (
        <InfoPanel title="Review your quote">
          {quoteLink ? (
            <InfoLink href={toAbsoluteUrl(quoteLink)}>
              Open your quote (OneDrive)
            </InfoLink>
          ) : (
            <p className="text-sm text-muted-foreground">
              Your agent has not uploaded the quote link yet.
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            If the link does not open, copy/paste it into a new tab.
          </p>
        </InfoPanel>
      )}

      {showQuoteAccepted && (
        <InfoPanel title="Quote accepted">
          <p className="text-sm text-muted-foreground">
            Thanks. We will proceed. Your agent {agentDetails?.name || "-"} will
            follow up shortly.
          </p>
        </InfoPanel>
      )}

      {showCleaner && cleanerBooking && (
        <InfoPanel title="Cleaner booking">
          <InfoList>
            <InfoItem label="Company">
              {cleanerBooking.company_name || "-"}
            </InfoItem>
            <InfoItem label="When">
              {fmtDateTime(cleanerBooking.date, cleanerBooking.time) || "-"}
            </InfoItem>
            <InfoItem label="Contact">{cleanerBooking.contact_number}</InfoItem>
            <InfoItem label="Notes">{cleanerBooking.notes}</InfoItem>
          </InfoList>
        </InfoPanel>
      )}

      {showRubbish && rubbishBooking && (
        <InfoPanel title="Rubbish collection">
          <InfoList>
            <InfoItem label="Company">
              {rubbishBooking.booking_company || "-"}
            </InfoItem>
            <InfoItem label="When">
              {fmtDateTime(
                rubbishBooking.booking_date,
                rubbishBooking.booking_time,
              ) || "-"}
            </InfoItem>
            <InfoItem label="Pickup">{rubbishBooking.pickup_address}</InfoItem>
            <InfoItem label="Contact">
              {[rubbishBooking.contact_name, rubbishBooking.contact_phone]
                .filter(Boolean)
                .join(", ")}
            </InfoItem>
            <InfoItem label="Ref #">{rubbishBooking.reference_number}</InfoItem>
            <InfoItem label="Notes">{rubbishBooking.notes}</InfoItem>
          </InfoList>
        </InfoPanel>
      )}

      {currentTask && taskKind === "lease" && currentTask?.lease_info?.link && (
        <InfoPanel title="Lease document">
          <InfoList>
            <InfoItem label="Provider / Agent">
              {currentTask.lease_info.provider}
            </InfoItem>
            <InfoItem label="Address">
              {currentTask.lease_info.address}
            </InfoItem>
            <InfoItem label="Start Date">
              {currentTask.lease_info.startDate}
            </InfoItem>
            <InfoItem label="End Date">
              {currentTask.lease_info.endDate}
            </InfoItem>
            <InfoItem label="Rent">
              {formatCurrencyLike(currentTask.lease_info.rentAmount)}
            </InfoItem>
            <InfoItem label="Bond">
              {formatCurrencyLike(currentTask.lease_info.bondAmount)}
            </InfoItem>
            <InfoItem label="Notes">{currentTask.lease_info.notes}</InfoItem>
          </InfoList>
          <InfoLink href={toAbsoluteUrl(currentTask.lease_info.link)}>
            View Lease on OneDrive
          </InfoLink>
        </InfoPanel>
      )}

      {currentTask && taskKind === "hotel_transfer" && hotelTransfer && (
        <InfoPanel title="Hotel transfer">
          <InfoList>
            <InfoItem label="Company">
              {hotelTransfer.booking_company || "-"}
            </InfoItem>
            <InfoItem label="When">
              {formatPickupDateTime(hotelTransfer.pickup_datetime) || "-"}
            </InfoItem>
            <InfoItem label="Pickup">{hotelTransfer.pickup_from}</InfoItem>
            <InfoItem label="Dropoff">{hotelTransfer.dropoff_to}</InfoItem>
            <InfoItem label="Passengers">
              {Array.isArray(hotelTransfer.passengers) &&
              hotelTransfer.passengers.length > 0
                ? hotelTransfer.passengers.join(" · ")
                : [hotelTransfer.passenger_name, hotelTransfer.passenger_phone]
                    .filter(Boolean)
                    .join(" · ")}
            </InfoItem>
            <InfoItem label="Confirmation #">
              {hotelTransfer.confirmation_no}
            </InfoItem>
            <InfoItem label="Notes">{hotelTransfer.notes}</InfoItem>
          </InfoList>
        </InfoPanel>
      )}

      {showElevator && elevatorAccess && (
        <InfoPanel title="Elevator access">
          <InfoList>
            <InfoItem label="Building">{elevatorAccess.building_name}</InfoItem>
            <InfoItem label="When">
              {fmtDateTime(
                elevatorAccess.booking_date,
                elevatorAccess.booking_time,
              ) || "-"}
            </InfoItem>
            <InfoItem label="Contact">
              {[elevatorAccess.contact_person, elevatorAccess.contact_phone]
                .filter(Boolean)
                .join(" · ")}
            </InfoItem>
            <InfoItem label="Instructions">
              {elevatorAccess.access_instructions}
            </InfoItem>
            <InfoItem label="Notes">{elevatorAccess.notes}</InfoItem>
          </InfoList>
        </InfoPanel>
      )}

      {showDeclutter && declutterBooking && (
        <InfoPanel title="Decluttering">
          <InfoList>
            <InfoItem label="Company">
              {declutterBooking.company_name || "-"}
            </InfoItem>
            <InfoItem label="When">
              {fmtDateTime(declutterBooking.date, declutterBooking.time) || "-"}
            </InfoItem>
            <InfoItem label="Contact">
              {declutterBooking.contact_number}
            </InfoItem>
            <InfoItem label="Areas">
              {Array.isArray(declutterBooking.areas) &&
              declutterBooking.areas.length > 0
                ? declutterBooking.areas.join(", ")
                : null}
            </InfoItem>
            <InfoItem label="Estimate">
              {declutterBooking.estimate_hours != null
                ? `${declutterBooking.estimate_hours} hour(s)`
                : null}
            </InfoItem>
            <InfoItem label="Notes">{declutterBooking.notes}</InfoItem>
          </InfoList>
        </InfoPanel>
      )}

      {showRentalApplyAdvice && (
        <InfoPanel title={ADVICE_RENTAL_APP_CONTENT.heading}>
          <Bullets items={ADVICE_RENTAL_APP_CONTENT.bullets} />
          <SectionHeading>
            {ADVICE_RENTAL_APP_CONTENT.followHeading}
          </SectionHeading>
          <Bullets items={ADVICE_RENTAL_APP_CONTENT.followBullets} />
          {agentDetails?.email && (
            <p className="mt-3 text-sm text-muted-foreground">
              Let your agent know via Slack or email{" "}
              <a
                href={`mailto:${agentDetails.email}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {agentDetails.email}
              </a>{" "}
              once you have submitted.
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            {ADVICE_RENTAL_APP_CONTENT.extraNote}
          </p>
        </InfoPanel>
      )}

      {showBoxDelivery && (
        <InfoPanel title="Box delivery">
          <InfoList>
            <InfoItem label="Company">{boxDelivery.delivery_company}</InfoItem>
            <InfoItem label="When">
              {fmtDateTime(
                boxDelivery.delivery_date,
                boxDelivery.delivery_time,
              ) || "-"}
            </InfoItem>
            <InfoItem label="Drop-off">{boxDelivery.dropoff_address}</InfoItem>
            <InfoItem label="Boxes">{boxDelivery.box_count}</InfoItem>
            <InfoItem label="Reference">
              {boxDelivery.reference_number}
            </InfoItem>
            <InfoItem label="Contact">{boxDelivery.contact_name}</InfoItem>
            <InfoItem label="Notes">{boxDelivery.notes}</InfoItem>
          </InfoList>
        </InfoPanel>
      )}
    </div>
  );
}

const InfoPanel = ({ title, children }) => (
  <section className="rounded-md border bg-muted/25 p-4">
    {title ? (
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    ) : null}
    <div className={title ? "mt-2" : ""}>{children}</div>
  </section>
);

const SectionHeading = ({ children }) => (
  <div className="mt-4 mb-1 text-sm font-semibold text-foreground">
    {children}
  </div>
);

const Bullets = ({ items = [] }) => (
  <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const InfoList = ({ children, className = "" }) => (
  <ul className={`space-y-1 text-sm text-muted-foreground ${className}`}>
    {children}
  </ul>
);

const InfoItem = ({ label, children }) => {
  if (children === null || children === undefined || children === "")
    return null;

  return (
    <li>
      <span className="font-medium text-foreground">{label}:</span> {children}
    </li>
  );
};

const InfoLink = ({ href, children }) => {
  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="mt-2 inline-block break-words text-sm font-medium text-primary underline-offset-4 hover:underline"
    >
      {children}
    </a>
  );
};

const MetricPills = ({ item = {} }: any) => {
  const metrics = [
    item.bedrooms != null ? `Beds: ${item.bedrooms}` : null,
    item.bathrooms != null ? `Baths: ${item.bathrooms}` : null,
    item.car_spaces != null ? `Car: ${item.car_spaces}` : null,
  ].filter(Boolean);

  if (!metrics.length) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {metrics.map((metric) => (
        <span
          key={metric}
          className="rounded-md border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
        >
          {metric}
        </span>
      ))}
    </div>
  );
};

const formatCurrencyLike = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return typeof value === "number" ? `AUD $${value.toLocaleString()}` : value;
};

const formatPickupDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};
